/**
 * Generic beta-feature gating for the frontend (mirrors backend utils/betaUsers.js).
 *
 * A gated feature shows for a user when EITHER:
 *   - REACT_APP_BETA_FEATURES_FOR_ALL === "true" (post-approval global flip), OR
 *   - the user's Auth0 sub or email is listed in REACT_APP_BETA_USER_IDS
 *     (comma-separated subs and/or emails).
 *
 * Defaults (empty list, flag off) gate NO user in, so existing users see no
 * change. Names stay generic (BETA_*) — Instagram is just the first consumer.
 */
import { useAuth0 } from "@auth0/auth0-react";

const stripPrefix = (id: string): string => {
  const i = id.indexOf("|");
  return i !== -1 ? id.substring(i + 1) : id;
};

const parseList = (raw?: string): string[] =>
  (raw || "")
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);

export function isBetaUser(user?: { sub?: string; email?: string } | null): boolean {
  if (String(process.env.REACT_APP_BETA_FEATURES_FOR_ALL).toLowerCase() === "true") {
    return true;
  }
  const allow = parseList(process.env.REACT_APP_BETA_USER_IDS);
  if (allow.length === 0) return false;

  const candidates = new Set<string>();
  if (user?.sub) {
    const sub = user.sub.toLowerCase();
    candidates.add(sub);
    candidates.add(stripPrefix(sub));
  }
  if (user?.email) candidates.add(user.email.toLowerCase());

  return allow.some((entry) => candidates.has(entry) || candidates.has(stripPrefix(entry)));
}

/** Hook: is the current Auth0 user a beta user? */
export function useIsBetaUser(): boolean {
  const { user } = useAuth0();
  return isBetaUser(user as { sub?: string; email?: string } | undefined);
}

/** Instagram is gated behind the generic beta flag for now. */
export function useInstagramEnabled(): boolean {
  return useIsBetaUser();
}
